'use client';

import { X } from 'lucide-react';
import type { ScreenerColumn } from '@/types/screener';
import { formatBound, humanizeText } from './format';

interface FilterChip {
  param: string;
  label: string;
}

const BOUND_PARAM = /^(.+)_(min|max)$/;

function describeFilters(searchParams: URLSearchParams, columnsByKey: Map<string, ScreenerColumn>): FilterChip[] {
  const chips: FilterChip[] = [];
  searchParams.forEach((value, param) => {
    if (!value) return;
    if (param === 'q') chips.push({ param, label: `Name or symbol: “${value}”` });
    else if (param === 'industry') chips.push({ param, label: `Industry: ${value}` });
    else if (param === 'exchange') chips.push({ param, label: `${value} only` });
    else if (param === 'score_status') chips.push({ param, label: `Score status: ${humanizeText(value)}` });
    else {
      const match = param.match(BOUND_PARAM);
      const column = match ? columnsByKey.get(match[1]) : undefined;
      const bound = Number(value);
      if (!match || !column || Number.isNaN(bound)) return;
      const symbol = match[2] === 'min' ? '≥' : '≤';
      chips.push({ param, label: `${column.label} ${symbol} ${formatBound(bound, column.unit)}` });
    }
  });
  return chips;
}

interface ActiveFilterChipsProps {
  searchParams: URLSearchParams;
  columnsByKey: Map<string, ScreenerColumn>;
  onRemove: (param: string) => void;
  onClearAll: () => void;
}

export default function ActiveFilterChips({ searchParams, columnsByKey, onRemove, onClearAll }: ActiveFilterChipsProps) {
  const chips = describeFilters(searchParams, columnsByKey);
  if (!chips.length) return null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400">Filters:</span>
      {chips.map((chip) => (
        <span
          key={chip.param}
          className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 py-1 pl-3 pr-1 text-xs font-medium text-emerald-800 dark:text-emerald-300"
        >
          {chip.label}
          <button
            type="button"
            onClick={() => onRemove(chip.param)}
            aria-label={`Remove filter ${chip.label}`}
            className="rounded-full p-0.5 hover:bg-emerald-500/20"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </span>
      ))}
      <button
        type="button"
        onClick={onClearAll}
        className="text-xs font-semibold text-zinc-500 hover:text-zinc-900 hover:underline dark:text-zinc-400 dark:hover:text-white"
      >
        Clear all
      </button>
    </div>
  );
}
